/**
 * Creates a polling utility with configurable retry behavior
 * @param {Object} options - Configuration options
 * @param {Function} options.callback - The function to call on each poll
 * @param {number} [options.interval=5000] - Polling interval in milliseconds
 * @param {boolean} [options.immediate=false] - Whether to execute the callback immediately on start
 * @param {Function} [options.validate] - Optional validation function to continue polling
 * @param {number} [options.maxRetries=0] - Maximum number of retry attempts on error (0 for infinite)
 * @param {Function} [options.onError] - Optional error handler
 */
export function createPolling({
  callback,
  interval = 5000,
  immediate = false,
  validate = null,
  maxRetries = 0,
  onError = null,
}) {
  let pollingId = null;
  let isPolling = false;
  let currentRetry = 0;

  const stop = () => {
    if (pollingId) {
      clearTimeout(pollingId);
      pollingId = null;
    }
    isPolling = false;
  };

  const poll = async () => {
    if (!isPolling) return;

    try {
      await callback();
      // Reset retry counter on successful execution
      currentRetry = 0;
    } catch (error) {
      console.error("Polling error:", error);

      // Handle error with retry logic
      if (onError) {
        onError(error, currentRetry);
      }

      // Check if we've reached max retries or retries disabled
      if (!maxRetries || (maxRetries > 0 && currentRetry >= maxRetries)) {
        console.error(`Max retries (${maxRetries}) reached, stopping polling`);
        stop();
        return;
      }

      currentRetry++;
    }

    if (isPolling && (!validate || validate())) {
      pollingId = setTimeout(poll, interval);
    } else {
      isPolling = false;
    }
  };

  const start = () => {
    if (isPolling) return;
    isPolling = true;
    currentRetry = 0; // Reset retry counter on start

    if (immediate) {
      poll();
    } else {
      pollingId = setTimeout(poll, interval);
    }
  };

  return {
    start,
    stop,
    get isPolling() {
      return isPolling;
    },
    get currentRetry() {
      return currentRetry;
    },
  };
}
